import { SummarySection } from "./SummarySection"
import { OrdersTable } from "./OrdersTable"
import { Button } from "@/components/ui/button"
import { Download } from "lucide-react"
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import { useSelector } from "react-redux"
import { driversProfiles } from "@/reducers/profilesSliceSelectors"
import { useAppSelector } from "@/reducers/hooks"
import { DateRangePicker } from "@/components/ui/DateRangePicker"
import { DriverFilter } from "./DriverFilter"
import { Element } from "@xmpp/xml"
import { selectTabs, TAB_TYPES, TabType } from "@/reducers/tabsSlice"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"

export interface SummaryData {
  total_expected_distance: number
  total_actual_distance: number
  total_actual_approach_distance: number
  total_expected_duration: number
  total_actual_duration: number
  total_actual_approach_duration: number
  total_expected_tolls: number
  total_actual_tolls: number
  // Actual tolls of only the orders that have expected tolls - what
  // total_expected_tolls compares against (absent from older lambdas).
  total_actual_tolls_with_expected?: number
  total_actual_approach_tolls: number
  total_price: number
}

export interface OrderData {
  driver_jid: string
  chat_jid: string
  expected_distance: number | null
  expected_duration: number | null
  actual_distance: number | null
  actual_duration: number | null
  actual_approach_distance: number | null
  actual_approach_duration: number | null
  start_timestamp: string
  end_timestamp: string
  route_timestamp: string
  first_stop_id: string
  last_stop_id: string
  // The route version gps planned the order from; the map loads it instead of
  // route_timestamp, whose version may not carry the order's stops.
  expected_route_driver_jid?: string | null
  expected_route_timestamp?: string | null
  expected_route_first_stop_id?: string | null
  expected_route_last_stop_id?: string | null
  expected_tolls_sum: number | null
  actual_tolls_sum: number | null
  actual_approach_tolls_sum: number | null
  price_netto_eur: number | null
  file_url: string
  file_name: string
  description: string | null
  message_timestamp: number
  metadata?: Record<string, unknown>
  delivery_order_id?: string
  ref_number?: string
  order_number?: string
  customer_nip?: string
  trailer_plates?: string
  driver_name?: string
  car_plates?: string
  customer_name?: string
  coordinator_name?: string
  empty_kilometers?: string
  loaded_kilometers?: string
  export_status?: "success" | "failed" | null
  export_external_id?: string | null
  export_error?: string | null
}

interface ApiResponse {
  orders: OrderData[]
  summary: SummaryData
}
function parseOrdersSummary(commandResult: Element) {
  const jsonElement = commandResult
    .getChild("command", "http://jabber.org/protocol/commands")
    ?.getChild("json", "urn:xmpp:json:0")

  if (!jsonElement) return undefined

  const jsonText = jsonElement.text()
  if (!jsonText) return undefined

  try {
    return JSON.parse(jsonText)
  } catch {
    return undefined
  }
}

export function DeliveryOrderReport({
  onNavigateToChat,
}: {
  onNavigateToChat: (jid: string, tab?: TabType) => void
}) {
  const { t } = useTranslation()
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [reportData, setReportData] = useState<ApiResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { client } = useContext(XmppContext)
  const drivers = useSelector(driversProfiles)

  const { activeTab } = useAppSelector(selectTabs)
  const [isExporting, setIsExporting] = useState(false)

  useLayoutEffect(() => {
    if (activeTab === TAB_TYPES.REPORTS) {
      setIsReportModalOpen(true)
    } else {
      setIsReportModalOpen(false)
    }
  }, [activeTab])

  // Filter state
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | null>(
    () => {
      const today = new Date()
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(today.getDate() - 7)
      return { from: thirtyDaysAgo, to: today }
    },
  )
  const [selectedChat, setSelectedChat] = useState<string>("all-drivers")

  const handleNavigateToChat = useCallback(
    async (jid: string) => {
      setIsReportModalOpen(false)
      onNavigateToChat(jid, TAB_TYPES.CHATS)
    },
    [onNavigateToChat],
  )

  const handleExport = useCallback(async () => {
    if (!client || !dateRange) return

    setIsExporting(true)

    try {
      const since = dateRange.from.toISOString()
      const untilDate = new Date(dateRange.to)
      untilDate.setDate(untilDate.getDate() + 1)
      const until = untilDate.toISOString()
      const chatJid = selectedChat === "all-drivers" ? "" : selectedChat

      const result = await AdHocCommands.getOrdersSummary(
        client,
        chatJid,
        since,
        until,
        "export_orders",
      )
      const data = parseOrdersSummary(result)
      console.log("Export result data:", data)

      if (!data || !data.download_url) {
        throw new Error(i18n.t("orders.report.exportUrlFailed"))
      }

      // Open the presigned S3 URL to download the CSV
      window.open(data.download_url, "_blank")
    } catch (err) {
      console.error("Error exporting orders:", err)
      setError(
        err instanceof Error
          ? err.message
          : i18n.t("orders.report.exportFailed"),
      )
    } finally {
      setIsExporting(false)
    }
  }, [client, dateRange, selectedChat])

  useEffect(() => {
    if (!isReportModalOpen || !client || !dateRange) return

    const fetchReportData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Format dates as ISO strings for the API
        const since = dateRange.from.toISOString()

        // Add one day to 'to' date for inclusive range (backend expects next day)
        const untilDate = new Date(dateRange.to)
        untilDate.setDate(untilDate.getDate() + 1)
        const until = untilDate.toISOString()

        // When "all-drivers" is selected, pass empty string to get all orders
        // Otherwise, use the selected chat JID directly (it's already the transformed JID from the map key)
        const chatJid = selectedChat === "all-drivers" ? "" : selectedChat

        const result = await AdHocCommands.getOrdersSummary(
          client,
          chatJid,
          since,
          until,
          "get_summary_and_orders",
        )
        const data = parseOrdersSummary(result)

        if (!data) {
          throw new Error(i18n.t("orders.report.parseFailed"))
        }
        console.log(data)
        setReportData(data)
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : i18n.t("orders.report.fetchFailed"),
        )
        console.error("Error fetching delivery orders report:", err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchReportData()
  }, [isReportModalOpen, client, dateRange, selectedChat])

  return (
    <>
      {isReportModalOpen &&
        createPortal(
          <div className="fixed inset-0 left-[49px] z-50 flex w-[100vw-48px] items-center justify-center rounded-md bg-background">
            <div className="relative h-full w-full">
              <div className="h-full w-full overflow-auto">
                <div className="p-4">
                  <div className="mb-8 flex items-center justify-between">
                    <h1 className="text-2xl font-bold">
                      {t("orders.report.title")}
                    </h1>
                    <div className="flex items-center gap-4">
                      <DateRangePicker
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        maxDays={92}
                      />
                      <DriverFilter
                        selectedChat={selectedChat}
                        onChatChange={setSelectedChat}
                      />
                      <Button
                        variant="outline"
                        onClick={handleExport}
                        disabled={isExporting || isLoading}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {isExporting
                          ? t("orders.report.exporting")
                          : t("orders.report.exportCsv")}
                      </Button>
                    </div>
                  </div>
                  {isLoading && (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-lg text-muted-foreground">
                        {t("orders.report.loading")}
                      </div>
                    </div>
                  )}
                  {error && (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-lg text-red-500">
                        {t("orders.report.error", { error })}
                      </div>
                    </div>
                  )}
                  {!isLoading && !error && reportData && (
                    <>
                      <SummarySection data={reportData.summary} />
                      <OrdersTable
                        orders={reportData.orders}
                        drivers={drivers}
                        onNavigateToChat={handleNavigateToChat}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
